import React, { useEffect, useState } from 'react'
import { Text, View, TextInput } from 'react-native'
import { FormFieldsModel, ModelAnswerFromProps } from '../models/StoreDetailModel'
import styles from './PonderateNumberFieldStyles'
import { updateFormAnswerSchedule } from '../../../../db-sqlite/repositories/schedule.repository'
import theme from '../../../../theme'
import { usePonderateGroup } from '../hooks/usePonderateGroup'
import { useValidationAlerts, ValidationParentContext } from './useValidationAlerts'

interface PonderateNumberFieldProps {
    // Numerador(es) + denominador(es) + resultado(s) de un mismo grupo ponderate_config.
    fields: FormFieldsModel[]
    form_id: string
    schedule_id: string
    store_id: string
    answerProp: ModelAnswerFromProps
    answers?: ModelAnswerFromProps[]
    checkComplete: (option?: ModelAnswerFromProps) => void
    keyboardHiddenState: boolean
    onFocus: (id: string) => void
    activeInputId: string
    parentContext?: ValidationParentContext
}

// Versión horizontal de InputNumberPriceField para fields con ponderate_config:
// en vez de una lista vertical (una fila por field), arma una grilla de hasta 3
// columnas por fila (numeradores, luego denominadores, luego resultado), y las
// que no entran bajan alineadas a la fila siguiente vía flexWrap.
export const PonderateNumberField = ({
    fields,
    form_id,
    schedule_id,
    store_id,
    answerProp,
    answers,
    checkComplete,
    keyboardHiddenState,
    onFocus,
    activeInputId,
    parentContext,
}: PonderateNumberFieldProps) => {
    const {
        getFieldSavedText,
        onFieldComplete,
        numeratorFields,
        denominatorFields,
        resultFields,
        resultText,
    } = usePonderateGroup({
        fields,
        answers,
        answerProp,
        form_id,
        schedule_id,
        store_id,
        checkComplete,
    })

    const orderedFields = [...numeratorFields, ...denominatorFields, ...resultFields]

    const addPercentSign = (value?: string): string | undefined => {
        if (value === undefined || value === null) return undefined
        const trimmed = value.toString().trim()
        if (trimmed === '') return undefined
        return trimmed + '%'
    }

    return (
        <View style={styles.grid}>
            {orderedFields.map((field, index) => {
                const isResult = field.ponderate_config?.role === 'result'
                return (
                    <PonderateNumberCell
                        key={field._id ?? index}
                        field={field}
                        form_id={form_id}
                        schedule_id={schedule_id}
                        store_id={store_id}
                        answerProp={answerProp}
                        checkComplete={onFieldComplete(field)}
                        keyboardHiddenState={keyboardHiddenState}
                        onFocus={onFocus}
                        activeInputId={activeInputId}
                        disabled={isResult}
                        initialValue={getFieldSavedText(field)}
                        computedValue={
                            isResult
                                ? addPercentSign(
                                      resultText ?? getFieldSavedText(field),
                                  )
                                : undefined
                        }
                        parentContext={parentContext}
                    />
                )
            })}
        </View>
    )
}

interface PonderateNumberCellProps {
    field: FormFieldsModel
    form_id: string
    schedule_id: string
    store_id: string
    answerProp: ModelAnswerFromProps
    checkComplete: (option?: ModelAnswerFromProps) => void
    keyboardHiddenState: boolean
    onFocus: (id: string) => void
    activeInputId: string
    disabled?: boolean
    initialValue?: string
    computedValue?: string
    parentContext?: ValidationParentContext
}

const PonderateNumberCell = ({
    field,
    form_id,
    schedule_id,
    store_id,
    answerProp,
    checkComplete,
    keyboardHiddenState,
    onFocus,
    activeInputId,
    disabled,
    initialValue,
    computedValue,
    parentContext,
}: PonderateNumberCellProps) => {
    const [text, setText] = useState('')
    const inputRef = React.useRef<TextInput>(null)
    const { checkAlerts } = useValidationAlerts()

    useEffect(() => {
        // computedValue (role "result") manda siempre, ver efecto de abajo.
        if (computedValue !== undefined) return
        if (initialValue && initialValue !== '') {
            setText(initialValue)
        }
    }, [field._id, initialValue])

    useEffect(() => {
        if (computedValue === undefined) return
        setText(computedValue ?? '')
    }, [computedValue])

    useEffect(() => {
        if (disabled) return
        if (keyboardHiddenState && activeInputId == field._id) {
            if (text !== '') {
                validateAnswer(text)
            }
        }
    }, [keyboardHiddenState])

    const validateAnswer = async (value: string) => {
        if (disabled) return
        if (value.trim() === '') return
        const action = await checkAlerts(field, value, parentContext)
        if (action === 'reset') {
            setText('')
            return
        }
        create_answer(value)
    }

    const create_answer = (inputText: string) => {
        const pondera = field?.questionScore && field?.questionScore > 0 || field?.score
        const answer: ModelAnswerFromProps = {
            _id: field._id,
            option_selected_text: answerProp.option_selected_text,
            option_selected_id: answerProp.option_selected_id,
            question_id: answerProp.question_id,
            media: '',
            question_text: answerProp.question_text,
            // score: answerProp.score ? answerProp.score.toString() : '0',
            score: field?.score ? field?.score.toString() : '0',
            status: 1,
            date_finish: new Date().toString(),
            date_init: new Date().toString(),
            question_input: '',
            field_media: '',
            field_text: inputText,
            // questionScore: answerProp.questionScore,
            questionScore: field?.questionScore ?? 0,
            index: answerProp.index,
            type: answerProp.type,
            ponderar: !!pondera,

            question: field?.label || field?.title || '',
            questionOptionSelected: answerProp?.option_selected_id,
            questionOptionSelectedText: answerProp?.option_selected_text,
            questionType: (field as any).type,
            questionPrice: inputText,
            tag: '',
            questionImage: '',
            questionInput: inputText,

            sku: answerProp.sku,
            description: answerProp.description ?? '',
            description_short: answerProp.description_short ?? '',
        }

        updateFormAnswerSchedule({
            newAnswer: answer,
            schedule_id,
            form_id,
            store_id,
        })
        checkComplete(answer)
    }

    return (
        <View style={styles.cell}>
            {field.label ? (
                <Text style={styles.label} numberOfLines={1}>
                    {field.label}
                </Text>
            ) : null}
            <TextInput
                ref={inputRef}
                style={[styles.box, disabled && styles.boxDisabled]}
                placeholder={!disabled ? '----' : '%'}
                placeholderTextColor={
                    !disabled
                        ? theme.colors.inputPlaceholder
                        : theme.colors.colorPrimary
                }
                value={text}
                editable={!disabled}
                onChangeText={(value) => {
                    if (disabled) return
                    setText(value)
                }}
                keyboardType='number-pad'
                inputMode='decimal'
                onEndEditing={(e) => validateAnswer(e.nativeEvent.text)}
                onSubmitEditing={(e) => validateAnswer(e.nativeEvent.text)}
                onFocus={() => {
                    if (disabled) return
                    onFocus(field._id)
                }}
                autoFocus={!disabled && activeInputId === field._id}
            />
        </View>
    )
}
